import { z } from 'zod';

import { GQL } from './graphql-exports';
import { CustomOperatorValue } from './shared/report';

export const ReportFilterOperatorSchema = {
  Boolean: z.enum([
    'is',
    'isNot',
    CustomOperatorValue.BooleanIsEmpty,
    CustomOperatorValue.BooleanIsNotEmpty,
  ]),
  ContractType: z.enum([
    CustomOperatorValue.ContractType,
    CustomOperatorValue.ContractTypeIsEmpty,
    CustomOperatorValue.ContractTypeIsNotEmpty,
    CustomOperatorValue.ContractTypeNotAnyOf,
  ]),
  Date: z.enum(['is', 'not', 'before', 'after', 'onOrAfter', 'onOrBefore']),
  DocumentStatus: z.enum([CustomOperatorValue.DocumentStatus]),
  FileType: z.enum([CustomOperatorValue.FileType, CustomOperatorValue.FileTypeNotAnyOf]),
  General: z.enum(['isEmpty', 'isNotEmpty']),
  Label: z.enum([
    CustomOperatorValue.Label,
    CustomOperatorValue.LabelNotAnyOf,
    CustomOperatorValue.LabelIsEmpty,
    CustomOperatorValue.LabelIsNotEmpty,
  ]),
  Numeric: z.enum(['=', '!=', '>', '>=', '<', '<=']),
  Owner: z.enum([CustomOperatorValue.Owner, CustomOperatorValue.OwnerNotAnyOf]),
  Party: z.enum([
    CustomOperatorValue.Party,
    CustomOperatorValue.PartyIsEmpty,
    CustomOperatorValue.PartyIsNotEmpty,
    CustomOperatorValue.PartyNotAnyOf,
  ]),
  RowStatus: z.enum([CustomOperatorValue.RowStatusAnyOf, CustomOperatorValue.RowStatusNotAnyOf]),
  SignatureDetected: z.enum([CustomOperatorValue.SignatureDetected]),
  SingleSelect: z.enum([
    'is',
    'not',
    'isAnyOf',
    'isEmpty',
    'isNotEmpty',
    CustomOperatorValue.QuestionSelectNotAnyOf,
  ]),
  String: z.enum([
    'equals',
    'startsWith',
    'endsWith',
    'contains',
    'isAnyOf',
    'isEmpty',
    'isNotEmpty',
    'doesNotContain',
    'doesNotEqual',
  ]),
};

export const AllReportFilterOperatorSchemas = z.union([
  ReportFilterOperatorSchema.Boolean,
  ReportFilterOperatorSchema.ContractType,
  ReportFilterOperatorSchema.Date,
  ReportFilterOperatorSchema.DocumentStatus,
  ReportFilterOperatorSchema.FileType,
  ReportFilterOperatorSchema.General,
  ReportFilterOperatorSchema.Label,
  ReportFilterOperatorSchema.Numeric,
  ReportFilterOperatorSchema.Owner,
  ReportFilterOperatorSchema.Party,
  ReportFilterOperatorSchema.RowStatus,
  ReportFilterOperatorSchema.SignatureDetected,
  ReportFilterOperatorSchema.String,
  ReportFilterOperatorSchema.SingleSelect,
]);

export const ReportFilterValueSchema = {
  Boolean: z.enum(['true', 'false', 'any']).default('any'),
};

const ValueUserSchema = z.object({
  __typename: z.literal('User'),
  displayName: z.string(),
  email: z.string(),
  firstName: z.string().nullish(),
  id: z.string(),
});
const ValuePartySchema = z.object({
  __typename: z.literal('Party'),
  description: z.string().nullish(),
  id: z.string(),
  name: z.string(),
});
const ValueContractTypeSchema = z.object({
  __typename: z.literal('ContractType'),
  id: z.string(),
  value: z.string(),
});
const ValueLabelSchema = z.object({
  __typename: z.literal('Label'),
  id: z.string(),
  labelText: z.string(),
});
const ValueRowStatusSchema = z.enum([
  GQL.RowStatus.AiRunning,
  GQL.RowStatus.AiNotRun,
  GQL.RowStatus.AiError,
  GQL.RowStatus.AnswerStale,
  GQL.RowStatus.OcrRunning,
  GQL.RowStatus.OcrError,
]);
const ValueFileTypeSchema = z.object({
  label: z.string(),
  mimetype: z.string().nullable(),
});
const ValueQuestionSchema = z
  .union([z.array(z.union([z.string(), z.number()])), z.string(), z.number()])
  .nullish();

export const ReportDocAttrString = z.enum(['id', 'name', 'description', 'status']);
export const ReportDocAttrDates = z.enum([
  'createdAt',
  'updatedAt',
  'deletedAt',
  'signedAt',
  'lastAccessedByRequesterAt',
]);

export const ReportGridFilterItemSchemas = {
  ContractType: z.object({
    field: z.literal('type'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.ContractType,
    value: z.array(ValueContractTypeSchema).optional(),
  }),
  Date: z.object({
    field: ReportDocAttrDates,
    id: z.union([z.string(), z.number()]).optional(),
    operator: z.union([ReportFilterOperatorSchema.Date, ReportFilterOperatorSchema.General]),
    value: z.string().nullish(),
  }),
  DocumentStatus: z.object({
    field: z.literal('status'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.DocumentStatus,
    value: z
      .array(
        z.enum([
          GQL.DocumentStatus.Draft,
          GQL.DocumentStatus.Signed,
          GQL.DocumentStatus.Template,
          GQL.DocumentStatus.Expired,
        ])
      )
      .optional(),
  }),
  Favorite: z.object({
    field: z.literal('requesterFavorited'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.Boolean,
    value: ReportFilterValueSchema.Boolean,
  }),
  FileType: z.object({
    field: z.literal('fileType'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.FileType,
    value: z.array(ValueFileTypeSchema).optional(),
  }),
  Label: z.object({
    field: z.literal('labels'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.Label,
    value: z.array(ValueLabelSchema).optional(),
  }),
  Owner: z.object({
    field: z.literal('owner'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.Owner,
    value: z.array(ValueUserSchema).optional(),
  }),
  Party: z.object({
    field: z.literal('parties'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.Party,
    value: z.array(ValuePartySchema).optional(),
  }),
  Question: z.object({
    field: z.uuid(),
    id: z.union([z.string(), z.number()]).optional(),
    operator: z.union([
      ReportFilterOperatorSchema.General,
      ReportFilterOperatorSchema.String,
      ReportFilterOperatorSchema.Date,
      ReportFilterOperatorSchema.Numeric,
      ReportFilterOperatorSchema.Boolean,
      ReportFilterOperatorSchema.SingleSelect,
    ]),
    value: ValueQuestionSchema,
  }),
  RowStatus: z.object({
    field: z.literal('rowStatus'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.RowStatus,
    value: z.array(ValueRowStatusSchema).optional(),
  }),
  /**
   * Documents where Aline AI found a signature on the latest version. Lives on
   * the Status column and takes no value, so it can never be half-filled.
   */
  SignatureDetected: z.object({
    field: z.literal('status'),
    id: z.union([z.string(), z.number()]).optional(),
    operator: ReportFilterOperatorSchema.SignatureDetected,
    value: z.unknown().optional(),
  }),
  String: z.object({
    field: ReportDocAttrString,
    id: z.union([z.string(), z.number()]).optional(),
    operator: z.union([ReportFilterOperatorSchema.String, ReportFilterOperatorSchema.General]),
    value: z.union([z.string().nullish(), z.array(z.string()).optional()]),
  }),
};

export const isReportGridFilterItem = {
  ContractType: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.ContractType> =>
    ReportGridFilterItemSchemas.ContractType.safeParse(item).success,
  Date: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Date> =>
    ReportGridFilterItemSchemas.Date.safeParse(item).success,
  DocumentStatus: (
    item: unknown
  ): item is z.infer<typeof ReportGridFilterItemSchemas.DocumentStatus> =>
    ReportGridFilterItemSchemas.DocumentStatus.safeParse(item).success,
  Favorite: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Favorite> =>
    ReportGridFilterItemSchemas.Favorite.safeParse(item).success,
  FileType: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.FileType> =>
    ReportGridFilterItemSchemas.FileType.safeParse(item).success,
  Label: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Label> =>
    ReportGridFilterItemSchemas.Label.safeParse(item).success,
  Owner: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Owner> =>
    ReportGridFilterItemSchemas.Owner.safeParse(item).success,
  Party: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Party> =>
    ReportGridFilterItemSchemas.Party.safeParse(item).success,
  Question: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.Question> =>
    ReportGridFilterItemSchemas.Question.safeParse(item).success,
  RowStatus: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.RowStatus> =>
    ReportGridFilterItemSchemas.RowStatus.safeParse(item).success,
  SignatureDetected: (
    item: unknown
  ): item is z.infer<typeof ReportGridFilterItemSchemas.SignatureDetected> =>
    ReportGridFilterItemSchemas.SignatureDetected.safeParse(item).success,
  String: (item: unknown): item is z.infer<typeof ReportGridFilterItemSchemas.String> =>
    ReportGridFilterItemSchemas.String.safeParse(item).success,
};

export const ReportGridFilterItemWithoutQuestionSchema = z.union([
  ReportGridFilterItemSchemas.ContractType,
  ReportGridFilterItemSchemas.Date,
  ReportGridFilterItemSchemas.DocumentStatus,
  ReportGridFilterItemSchemas.Favorite,
  ReportGridFilterItemSchemas.FileType,
  ReportGridFilterItemSchemas.Label,
  ReportGridFilterItemSchemas.Owner,
  ReportGridFilterItemSchemas.Party,
  ReportGridFilterItemSchemas.RowStatus,
  ReportGridFilterItemSchemas.SignatureDetected,
  ReportGridFilterItemSchemas.String,
]);
export const ReportGridFilterItemSchema = z.union([
  ReportGridFilterItemWithoutQuestionSchema,
  ReportGridFilterItemSchemas.Question,
]);
export type ReportGridFilterItem = z.infer<typeof ReportGridFilterItemSchema>;

type ReportGridFilterValue<TSchema extends { value?: unknown }> =
  NonNullable<TSchema['value']> extends (infer TItem)[] ? TItem : never;

export type ReportGridFilterContractTypeValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.ContractType>
>;
export type ReportGridFilterDocumentStatusValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.DocumentStatus>
>;
export type ReportGridFilterFileTypeValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.FileType>
>;
export type ReportGridFilterLabelValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.Label>
>;
export type ReportGridFilterOwnerValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.Owner>
>;
export type ReportGridFilterPartyValue = ReportGridFilterValue<
  z.infer<typeof ReportGridFilterItemSchemas.Party>
>;

export type ReportTypedFilterItemColumnField = z.infer<
  typeof ReportGridFilterItemWithoutQuestionSchema
>['field'];

export const ReportGridFilterModelSchema = z.object({
  items: z.array(ReportGridFilterItemSchema),
  logicOperator: z.enum(['and', 'or']).optional(),
  quickFilterLogicOperator: z.enum(['and', 'or']).optional(),
  quickFilterValues: z.array(z.string()).optional(),
});
export type ReportGridFilterModel = z.infer<typeof ReportGridFilterModelSchema>;
